import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ApiErrorAlert from '../common/ApiErrorAlert';
import AuthPageShell, {
  authInputClass,
  authLabelClass,
  authPrimaryButtonClass,
} from './AuthPageShell';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [grade, setGrade] = useState('');
  const [error, setError] = useState('');
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }

    if (!grade) {
      setError('الرجاء اختيار الصف الدراسي');
      return;
    }

    if (!phoneNumber) {
      setError('رقم الهاتف مطلوب');
      return;
    }

    if (!parentPhone) {
      setError('هاتف ولي الأمر مطلوب');
      return;
    }

    // Convert grade format for API
    let academicGrade = grade;
    if (grade === "الصف الأول الثانوي") academicGrade = "Secondary1";
    if (grade === "الصف الثاني الثانوي") academicGrade = "Secondary2";
    if (grade === "الصف الثالث الثانوي") academicGrade = "Secondary3";

    try {
      const formData = new FormData();
      formData.append('UserName', name);
      formData.append('Password', password);
      formData.append('Email', email);
      formData.append('PhoneNumber', phoneNumber);
      formData.append('AcademicYear', academicGrade);
      formData.append('ConfirmPassword', confirmPassword);
      formData.append('ParentPhone', parentPhone);

      const result = await register(formData);
      
      if (result.success) {
        // Navigate directly to login page with success message
        navigate('/login', {
          state: {
            verificationSuccess: true,
            message: 'تم إنشاء الحساب بنجاح! يمكنك الآن تسجيل الدخول.',
            email: email
          }
        });
      } else {
        setError(result.error || 'فشل إنشاء الحساب. الرجاء المحاولة مرة أخرى.');
      }
    } catch (error) {
      setError('حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
    }
  };

  const handleRetry = () => {
    setError('');
  };

  return (
    <AuthPageShell
      eyebrow="إنشاء حساب"
      title="انضم للمنصة"
      description="سجّل بياناتك وابدأ رحلة التعلم مع كورسات مرتّبة على صفّك"
      maxWidthClass="max-w-xl"
      footer={
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-brand-100" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-slate-500">لديك حساب بالفعل؟</span>
            </div>
          </div>
          <div className="mt-5 text-center">
            <Link
              to="/login"
              className="text-sm font-semibold text-brand-600 transition-colors hover:text-brand-700"
            >
              تسجيل الدخول
            </Link>
          </div>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <ApiErrorAlert message={error} onRetry={handleRetry} />}

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="name" className={authLabelClass}>
              الاسم الكامل
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={authInputClass}
              placeholder="أدخل اسمك الكامل"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="email" className={authLabelClass}>
              البريد الإلكتروني
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={authInputClass}
              placeholder="أدخل بريدك الإلكتروني"
            />
          </div>

          <div>
            <label htmlFor="phoneNumber" className={authLabelClass}>
              رقم الهاتف
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              required
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={authInputClass}
              placeholder="أدخل رقم هاتفك"
            />
          </div>

          <div>
            <label htmlFor="parentPhone" className={authLabelClass}>
              هاتف ولي الأمر
            </label>
            <input
              id="parentPhone"
              name="parentPhone"
              type="tel"
              required
              value={parentPhone}
              onChange={(e) => setParentPhone(e.target.value)}
              className={authInputClass}
              placeholder="أدخل رقم هاتف ولي الأمر"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="grade" className={authLabelClass}>
              الصف الدراسي
            </label>
            <select
              id="grade"
              name="grade"
              required
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className={authInputClass}
            >
              <option value="">اختر صفك الدراسي</option>
              <option value="الصف الأول الثانوي">الصف الأول الثانوي</option>
              <option value="الصف الثاني الثانوي">الصف الثاني الثانوي</option>
              <option value="الصف الثالث الثانوي">الصف الثالث الثانوي</option>
            </select>
          </div>

          <div>
            <label htmlFor="password" className={authLabelClass}>
              كلمة المرور
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={authInputClass}
              placeholder="أنشئ كلمة مرور"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className={authLabelClass}>
              تأكيد كلمة المرور
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={authInputClass}
              placeholder="أكد كلمة المرور"
            />
          </div>
        </div>

        <button type="submit" disabled={isLoading} className={authPrimaryButtonClass}>
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              جاري إنشاء الحساب...
            </span>
          ) : (
            'إنشاء الحساب'
          )}
        </button>
      </form>
    </AuthPageShell>
  );
};

export default Register; 